/**
 * Client-side mirror of backend/services/gtm/progress.py.
 *
 * Kept intentionally byte-close to the Python module so any status
 * enum change gets caught by both codepaths at review time.
 */

export const STATUSES = [
    "COMPLETE",
    "COMPLETE_WITH_EXCEPTION",
    "IN_PROGRESS",
    "FOUNDER_REVIEW",
    "WAITING_EXTERNAL",
    "BLOCKED",
    "PLANNED",
    "LOCKED",
    "DEFERRED",
    "NOT_STARTED",
];

const _STATUS_PROGRESS = {
    COMPLETE: 1.0,
    COMPLETE_WITH_EXCEPTION: 1.0,
    IN_PROGRESS: 0.5,
    FOUNDER_REVIEW: 0.75,
    WAITING_EXTERNAL: 0.5,
    BLOCKED: 0.25,
    PLANNED: 0.0,
    LOCKED: 0.0,
    DEFERRED: 0.0,
    NOT_STARTED: 0.0,
};

export function statusProgress(status) {
    return _STATUS_PROGRESS[status] ?? 0.0;
}

export function weightedProgress(rows) {
    let totalW = 0;
    let achieved = 0;
    for (const r of rows) {
        const w = r.progress_weight === undefined || r.progress_weight === null
            ? 1
            : Number(r.progress_weight);
        if (w <= 0) continue;
        totalW += w;
        achieved += w * statusProgress(r.status);
    }
    return totalW === 0 ? 0 : achieved / totalW;
}

export function phaseStatus(children) {
    if (!children || children.length === 0) return "PLANNED";
    if (children.some((c) => c.status === "BLOCKED" && c.launch_critical)) return "BLOCKED";
    if (children.some((c) => c.status === "IN_PROGRESS")) return "IN_PROGRESS";
    const required = children.filter((c) => !["LOCKED", "DEFERRED"].includes(c.status));
    if (required.length > 0) {
        if (required.every((c) => c.status === "COMPLETE")) return "COMPLETE";
        if (
            required.every((c) => ["COMPLETE", "COMPLETE_WITH_EXCEPTION"].includes(c.status)) &&
            required.some((c) => c.status === "COMPLETE_WITH_EXCEPTION")
        ) return "COMPLETE_WITH_EXCEPTION";
        if (required.some((c) => c.status === "FOUNDER_REVIEW")) return "FOUNDER_REVIEW";
        if (required.some((c) => c.status === "WAITING_EXTERNAL")) return "WAITING_EXTERNAL";
        if (required.some((c) => c.status === "BLOCKED")) return "BLOCKED";
        return "PLANNED";
    }
    if (children.every((c) => c.status === "LOCKED")) return "LOCKED";
    return "PLANNED";
}

export function domainReadiness(rows) {
    const buckets = new Map();
    for (const r of rows) {
        if (!r.domain) continue;
        if (!buckets.has(r.domain)) buckets.set(r.domain, []);
        buckets.get(r.domain).push(r);
    }
    const out = {};
    for (const [d, v] of buckets.entries()) out[d] = weightedProgress(v);
    return out;
}

export function summaryMetrics(rows) {
    const completed = rows.filter((r) => ["COMPLETE", "COMPLETE_WITH_EXCEPTION"].includes(r.status)).length;
    const active = rows.filter((r) => ["IN_PROGRESS", "FOUNDER_REVIEW", "WAITING_EXTERNAL"].includes(r.status)).length;
    const blocking = rows.filter((r) => r.status === "BLOCKED").length;
    const launchCriticalRemaining = rows.filter(
        (r) => r.launch_critical && !["COMPLETE", "COMPLETE_WITH_EXCEPTION"].includes(r.status),
    ).length;
    return {
        total_count: rows.length,
        completed_count: completed,
        active_count: active,
        blocking_count: blocking,
        launch_critical_remaining: launchCriticalRemaining,
        overall_readiness: weightedProgress(rows),
    };
}

// Ordering + presentation metadata for phase codes; unknown phases fall
// back to alphabetical order at the tail of the roadmap.
export const PHASE_ORDER = [
    "PHASE_1_FOUNDATION",
    "STAGE_B_GOVERNANCE",
    "PHASE_2E",
    "DEBT_A",
    "INTENT_INTEGRITY_GATE",
    "EKG",
    "ASCEND",
    "VOICE",
    "PRODUCTIZATION",
    "PRIVATE_ALPHA",
    "PUBLIC_BETA",
    "COMMERCIAL_READINESS",
    "LAUNCH_PREPARATION",
    "GO_TO_MARKET",
];

export const PHASE_TITLES = {
    PHASE_1_FOUNDATION: "Phase 1 · Foundation",
    STAGE_B_GOVERNANCE: "Stage B · Model & Evaluation Governance",
    PHASE_2E: "Phase 2E",
    DEBT_A: "Debt A · D1–D8",
    INTENT_INTEGRITY_GATE: "Intent Integrity Gate",
    EKG: "EKG",
    ASCEND: "ASCEND Implementation",
    VOICE: "Voice",
    PRODUCTIZATION: "Productization",
    PRIVATE_ALPHA: "Private Alpha",
    PUBLIC_BETA: "Public Beta",
    COMMERCIAL_READINESS: "Commercial Readiness",
    LAUNCH_PREPARATION: "Launch Preparation",
    GO_TO_MARKET: "Go To Market",
};

export const DOMAIN_TITLES = {
    CORE_INTELLIGENCE: "Core Intelligence",
    PRODUCT_EXPERIENCE: "Product Experience",
    SECURITY_GOVERNANCE: "Security & Governance",
    INFRASTRUCTURE: "Infrastructure",
    OPERATIONS: "Operations",
    COMMERCIAL_BILLING: "Commercial / Billing",
    LAUNCH_MARKETING: "Launch / Marketing",
};

export const DOMAIN_ORDER = [
    "CORE_INTELLIGENCE",
    "PRODUCT_EXPERIENCE",
    "SECURITY_GOVERNANCE",
    "INFRASTRUCTURE",
    "OPERATIONS",
    "COMMERCIAL_BILLING",
    "LAUNCH_MARKETING",
];

export function groupByPhase(rows) {
    const byPhase = new Map();
    for (const r of rows) {
        if (!byPhase.has(r.phase_code)) byPhase.set(r.phase_code, []);
        byPhase.get(r.phase_code).push(r);
    }
    const ordered = [];
    for (const p of PHASE_ORDER) if (byPhase.has(p)) ordered.push([p, byPhase.get(p)]);
    for (const [p, v] of byPhase.entries()) if (!PHASE_ORDER.includes(p)) ordered.push([p, v]);
    for (const [, v] of ordered) v.sort((a, b) => a.sort_order - b.sort_order);
    return ordered;
}
